import { Link } from "react-router-dom";
import type { Property } from "../data/properties";
import Badge from "./Badge";

type Props = {
    property: Property;
};

const STATUS_STYLES: Record<Property["status"], string> = {
    "For Sale": "bg-emerald-500/15 text-[rgb(var(--fg))] border-emerald-500/25",
    Pending: "bg-amber-500/15 text-[rgb(var(--fg))] border-amber-500/25",
    Sold: "bg-rose-500/15 text-[rgb(var(--fg))] border-rose-500/25",
};

export default function PropertyCard({ property }: Props): JSX.Element {
    const imageUrl = property.image.startsWith("/")
        ? property.image
        : `/static/properties/${property.shortId}/images/${property.image}`;

    const summaryText: string = (() => {
        if (property.kind === "residential") {
            return `${property.beds} bd • ${
                property.baths
            } ba • ${property.sqft.toLocaleString()} sqft`;
        }

        if (property.kind === "land") {
            return `${property.acres} acres`;
        }

        const parts: string[] = [];
        if (typeof property.beds === "number")
            parts.push(`${property.beds} bd`);
        if (typeof property.baths === "number")
            parts.push(`${property.baths} ba`);
        if (typeof property.sqft === "number")
            parts.push(`${property.sqft.toLocaleString()} sqft`);
        if (typeof property.acres === "number")
            parts.push(`${property.acres} acres`);
        return parts.join(" • ");
    })();

    return (
        <Link
            to={`/properties/${property.shortId}`}
            className="group relative overflow-hidden rounded-2xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--surface)/0.05)] shadow-sm transition hover:border-[rgb(var(--border)/0.15)] hover:bg-[rgb(var(--surface)/0.08)]"
        >
            <div className="absolute inset-0 opacity-0 transition group-hover:opacity-100">
                <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[rgb(var(--surface)/0.14)] blur-3xl" />
                <div className="absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-[rgb(var(--surface)/0.08)] blur-3xl" />
            </div>

            <div className="relative p-4">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-base font-semibold">
                            {property.title}
                        </div>
                        <div className="mt-1 text-sm text-[rgb(var(--muted))]">
                            {property.location}
                        </div>
                    </div>

                    <span
                        className={[
                            "rounded-full border px-2.5 py-1 text-xs whitespace-nowrap",
                            STATUS_STYLES[property.status],
                        ].join(" ")}
                    >
                        {property.status}
                    </span>
                </div>

                <div className="mt-4 overflow-hidden rounded-xl border border-[rgb(var(--border)/0.10)] bg-[rgb(var(--panel)/0.40)]">
                    <img
                        src={imageUrl}
                        alt={property.title}
                        className="h-44 w-full object-contain bg-black transition group-hover:scale-[1.02]"
                        width={1200}
                        height={600}
                    />
                </div>

                <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm font-semibold">
                        {property.priceLabel}
                    </div>
                    <div className="text-xs text-[rgb(var(--muted))]">
                        {summaryText}
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                    {property.tags.slice(0, 3).map((t) => (
                        <Badge key={t}>{t}</Badge>
                    ))}
                </div>
            </div>
        </Link>
    );
}
